import { useState, useRef, useEffect } from 'react';
import {
  Calendar, User, ChevronDown, Check, Search, Trash2
} from 'lucide-react';
import { Tooltip } from 'primereact/tooltip';
import { STATUS_CFG } from '../../../helpers/Project-Report/utils/statusConfig';
import { fmtDate, avatarColors } from '../../../helpers/Project-Report/utils/helpers';
import DateRangePanel from './DateRangePanel';
import SubCategoryDropdown from './SubCategoryDropdown';
import PicDropdown from './PicDropdown';
import ExternalPicDropdown from './ExternalPicDropdown';
import moment from 'moment';

const ListTab = ({ tasks, setTasks, accent, picOptions, picSearch, setPicSearch, onPicSearch, onRefreshPic, permissions }) => {
  const [newTaskName, setNewTaskName] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [openStatusId, setOpenStatusId] = useState(null);

  const [datePicker, setDatePicker] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // task object pending delete
  const listRef = useRef(null);

  // const isReadOnly = (isEdited === 'false' || isEdited === false) && (onIframe === 'true' || onIframe === true);
  const isReadOnly = !permissions.includes("project_report:edit");

  // Tutup semua dropdown saat klik di luar area tabel/dropdown aktif
  useEffect(() => {
    const handler = (e) => {
      if (listRef.current && !listRef.current.contains(e.target)) {
        setOpenStatusId(null);
        setDatePicker(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const updateTask = (id, updates) => {
    const today = moment().format('DD-MM-YYYY');
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...updates, lastUpdate: today } : t));
  };

  const handleDelete = (task) => setDeleteConfirm(task);

  const confirmDeleteTask = () => {
    if (deleteConfirm) {
      setTasks(prev => prev.filter(t => t.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    }
  };

  const handleAdd = (e) => {
    if (e.key !== 'Enter') return;
    const name = newTaskName.trim();
    if (!name) return;
    const today = moment().format('DD-MM-YYYY');
    setTasks(prev => {
      const validIds = prev.map(t => Number(t.id)).filter(n => !isNaN(n));
      const nextId = validIds.length > 0 ? Math.max(...validIds) + 1 : 1;
      return [...prev, {
        id: nextId,
        name, pic: '', subCategory: null, status: 'on_planning',
        scheduleStart: '', scheduleEnd: '', actualStart: '', actualEnd: '', progress: 0,
        lastUpdate: today,
      }];
    });
    setNewTaskName('');
  };

  const getProgressColor = (p) => {
    if (p === 100) return '#10b981';
    if (p >= 70) return '#6366f1';
    if (p >= 40) return '#f59e0b';
    if (p > 0) return accent;
    return '#e2e8f0';
  };

  // Small shared style for cell inputs/selects
  const cellInputStyle = {
    border: '1.5px solid #e2e8f0', borderRadius: '8px',
    padding: '6px 10px', fontSize: '12px', color: '#0f172a',
    background: '#fafafa', cursor: 'pointer', outline: 'none',
    width: '100%', transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: "'Inter', sans-serif",
  };

  return (
    <div ref={listRef}>
      {/* Table header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1.1fr) minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 1.1fr) minmax(0, 0.9fr) 60px minmax(0, 0.9fr)',
          gap: '8px', padding: '10px 20px', borderRadius: '10px',
          background: 'linear-gradient(135deg,#f8fafc,#f1f5f9)',
          marginBottom: '8px', fontSize: '11px', fontWeight: '700',
          color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px',
        }}
      >
        <span>Task</span>
        <span>PIC</span>
        {/* <span>Sub Categories</span> */}
        <span>PIC External</span>
        <span>Status</span>
        <span>Schedule Date</span>
        <span>Actual Date</span>
        <span>Progress</span>
        {!isReadOnly ? <span style={{ textAlign: 'center' }}>Action</span> : <span />}
        <span style={{ textAlign: 'center' }}>Last Update</span>
      </div>

      {/* Task rows */}
      {tasks.map((task, idx) => {
        const st = STATUS_CFG[task.status] || STATUS_CFG.on_planning;
        const pct = task.progress;
        const barColor = getProgressColor(pct);

        // Date picker open for this task/field?
        const dpSched = datePicker?.taskId === task.id && datePicker?.field === 'schedule';
        const dpActual = datePicker?.taskId === task.id && datePicker?.field === 'actual';

        const handleProgressChange = (val) => {
          if (val === '') {
            updateTask(task.id, { progress: '' });
            return;
          }
          let parsedVal = parseInt(val, 10);
          if (isNaN(parsedVal)) return;
          if (parsedVal > 100) parsedVal = 100;
          if (parsedVal < 0) parsedVal = 0;

          if (parsedVal === 100) {
            if (task.status !== 'done') {
              updateTask(task.id, { progress: parsedVal, status: 'done', _prevStatus: task.status });
            } else {
              updateTask(task.id, { progress: parsedVal });
            }
          } else {
            if (task.status === 'done') {
              updateTask(task.id, { progress: parsedVal, status: task._prevStatus || 'on_progress' });
            } else {
              updateTask(task.id, { progress: parsedVal });
            }
          }
        };

        return (
          <div
            key={task.id || `task-fallback-${idx}`}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1.1fr) minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 1.1fr) minmax(0, 0.9fr) 60px minmax(0, 0.9fr)',
              gap: '8px', alignItems: 'center',
              padding: '12px 20px',
              background: idx % 2 === 0 ? '#fff' : '#fafafa',
              borderRadius: '12px', marginBottom: '6px',
              border: '1px solid #f1f5f9',
              transition: 'box-shadow 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.07)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
          >
            {/* Task name */}
            <Tooltip
              target={`.taskname-tooltip-${task.id}`}
              position="top"
              mouseTrack
              mouseTrackTop={15}
              pt={{ text: { style: { fontSize: '11px', padding: '6px 10px', fontWeight: '500', maxWidth: '260px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } } }}
            />
            <input
              type="text"
              className={`taskname-tooltip-${task.id}`}
              data-pr-tooltip={task.name}
              value={task.name}
              readOnly={isReadOnly}
              onChange={(e) => !isReadOnly && updateTask(task.id, { name: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
              style={{
                fontSize: '12.5px', fontWeight: '600', color: '#0f172a',
                border: '1px solid transparent', background: 'transparent',
                padding: '4px 6px', borderRadius: '4px', outline: 'none',
                width: '100%', textOverflow: 'ellipsis', transition: 'all 0.2s',
                cursor: isReadOnly ? 'default' : 'text'
              }}
              onFocus={(e) => { if (!isReadOnly) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.border = '1px solid #e2e8f0'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)' } }}
              onBlur={(e) => { if (!isReadOnly) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.border = '1px solid transparent'; e.currentTarget.style.boxShadow = 'none' } }}
            />

            {/* ── PIC: Custom Dropdown with Search ── */}
            <PicDropdown
              taskId={task.id}
              value={task.pic}
              onChange={(picVal) => updateTask(task.id, { pic: picVal })}
              accent={accent}
              isReadOnly={isReadOnly}
              cellInputStyle={cellInputStyle}
              picOptions={picOptions}
              picSearch={picSearch}
              setPicSearch={setPicSearch}
              onPicSearch={onPicSearch}
              onRefreshPic={onRefreshPic}
              onOpen={() => { setDatePicker(null); setOpenStatusId(null); }}
            />

            {/* ── PIC External ── */}
            <ExternalPicDropdown
              taskId={`ext-${task.id}`}
              value={task.picExternal}
              onChange={(val) => updateTask(task.id, { picExternal: val })}
              accent={accent}
              isReadOnly={isReadOnly}
              cellInputStyle={cellInputStyle}
            />

            {/* ── Sub Category ── */}
            {/* <SubCategoryDropdown
              taskId={task.id}
              value={task.subCategory}
              onChange={(catId) => updateTask(task.id, { subCategory: catId })}
              accent={accent}
              isReadOnly={isReadOnly}
              cellInputStyle={cellInputStyle}
              onOpen={() => { setDatePicker(null); setOpenStatusId(null); setOpenPicId(null); }}
            /> */}

            {/* ── Status: custom dropdown ── */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => {
                  if (isReadOnly) return;
                  setDatePicker(null); // tutup date picker jika ada
                  setOpenStatusId(openStatusId === task.id ? null : task.id);
                }}
                style={{
                  ...cellInputStyle,
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: st.bg, color: st.color,
                  border: openStatusId === task.id ? `1.5px solid ${st.color}` : `1.5px solid ${st.color}40`,
                  boxShadow: openStatusId === task.id ? `0 0 0 3px ${st.color}18` : 'none',
                  fontWeight: '700',
                }}
              >
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: st.color, flexShrink: 0 }} />
                <span style={{ flex: 1, textAlign: 'left', fontSize: '11.5px' }}>{st.label}</span>
                <ChevronDown size={11} style={{ transform: openStatusId === task.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>

              {openStatusId === task.id && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                  zIndex: 300, background: '#fff', borderRadius: '12px',
                  border: '1.5px solid #e2e8f0', boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
                  overflow: 'hidden', animation: 'dropFadeIn 0.15s ease',
                }}>
                  {Object.entries(STATUS_CFG).map(([key, val]) => (
                    <div
                      key={key}
                      onClick={() => {
                        if (key === 'done') {
                          if (!task._prevProgress && task.progress !== 100) {
                            updateTask(task.id, { status: key, progress: 100, _prevProgress: task.progress });
                          } else {
                            updateTask(task.id, { status: key, progress: 100 });
                          }
                        } else {
                          if (task.status === 'done') {
                            updateTask(task.id, { status: key, progress: task._prevProgress || 0 });
                          } else {
                            updateTask(task.id, { status: key });
                          }
                        }
                        setOpenStatusId(null);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '9px 14px', cursor: 'pointer',
                        background: task.status === key ? val.bg : 'transparent',
                        transition: 'background 0.15s',
                        borderLeft: task.status === key ? `3px solid ${val.color}` : '3px solid transparent',
                      }}
                      onMouseEnter={(e) => { if (task.status !== key) e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { if (task.status !== key) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: val.color, flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', color: val.color, fontWeight: task.status === key ? '700' : '500' }}>{val.label}</span>
                      {task.status === key && <Check size={12} color={val.color} style={{ marginLeft: 'auto' }} />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Schedule Date: date-range picker ── */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => {
                  if (isReadOnly) return;
                  setOpenStatusId(null); // tutup status dropdown
                  setDatePicker(
                    dpSched ? null
                      : { taskId: task.id, field: 'schedule', start: task.scheduleStart || '', end: task.scheduleEnd || '' }
                  );
                }}
                style={{
                  ...cellInputStyle,
                  display: 'flex', alignItems: 'center', gap: '5px',
                  border: dpSched ? `1.5px solid ${accent}` : '1.5px solid #e2e8f0',
                  boxShadow: dpSched ? `0 0 0 3px ${accent}18` : 'none',
                  color: task.scheduleStart ? '#374151' : '#cbd5e1',
                  fontWeight: '500',
                }}
              >
                <Calendar size={11} color={accent} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {task.scheduleStart
                    ? `${fmtDate(task.scheduleStart)} – ${fmtDate(task.scheduleEnd)}`
                    : 'Pilih tanggal'}
                </span>
              </button>
              {dpSched && (
                <DateRangePanel
                  start={datePicker.start}
                  end={datePicker.end}
                  accent={accent}
                  onChange={(s, e) => setDatePicker(p => ({ ...p, start: s, end: e }))}
                  onApply={() => { updateTask(task.id, { scheduleStart: datePicker.start, scheduleEnd: datePicker.end }); setDatePicker(null); }}
                  onCancel={() => setDatePicker(null)}
                />
              )}
            </div>

            {/* ── Actual Date: date-range picker ── */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => {
                  if (isReadOnly) return;
                  setOpenStatusId(null); // tutup status dropdown
                  setDatePicker(
                    dpActual ? null
                      : { taskId: task.id, field: 'actual', start: task.actualStart || '', end: task.actualEnd || '' }
                  );
                }}
                style={{
                  ...cellInputStyle,
                  display: 'flex', alignItems: 'center', gap: '5px',
                  border: dpActual ? `1.5px solid ${accent}` : '1.5px solid #e2e8f0',
                  boxShadow: dpActual ? `0 0 0 3px ${accent}18` : 'none',
                  color: task.actualStart ? '#374151' : '#cbd5e1',
                  fontWeight: '500',
                }}
              >
                <Calendar size={11} color={accent} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {task.actualStart
                    ? `${fmtDate(task.actualStart)} – ${fmtDate(task.actualEnd)}`
                    : 'Pilih tanggal'}
                </span>
              </button>
              {dpActual && (
                <DateRangePanel
                  start={datePicker.start}
                  end={datePicker.end}
                  accent={accent}
                  onChange={(s, e) => setDatePicker(p => ({ ...p, start: s, end: e }))}
                  onApply={() => { updateTask(task.id, { actualStart: datePicker.start, actualEnd: datePicker.end }); setDatePicker(null); }}
                  onCancel={() => setDatePicker(null)}
                />
              )}
            </div>

            {/* Progress - Interactive Range Slider */}
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={pct}
                    readOnly={isReadOnly}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      handleProgressChange(val);
                    }}
                    style={{
                      width: '24px',
                      fontSize: '11px',
                      color: '#1e293b',
                      fontWeight: '700',
                      border: '1px solid transparent',
                      background: 'transparent',
                      padding: '2px 0',
                      borderRadius: '4px',
                      outline: 'none',
                      textAlign: 'right',
                      cursor: isReadOnly ? 'default' : 'text'
                    }}
                    onFocus={(e) => {
                      if (isReadOnly) return;
                      e.target.style.background = '#fff';
                      e.target.style.border = '1px solid #e2e8f0';
                    }}
                    onBlur={(e) => {
                      if (isReadOnly) return;
                      e.target.style.background = 'transparent';
                      e.target.style.border = '1px solid transparent';
                      if (pct === '') handleProgressChange('0');
                    }}
                    title={isReadOnly ? "" : "Ketik untuk mengubah progress"}
                  />
                  <span style={{ fontSize: '11px', color: '#1e293b', fontWeight: '700' }}>%</span>
                </div>
              </div>
              <div style={{ position: 'relative', height: '6px', background: '#334155', borderRadius: '99px', display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    position: 'absolute', top: 0, left: 0, bottom: 0,
                    width: `${pct === '' ? 0 : pct}%`,
                    background: accent,
                    borderRadius: '99px', transition: 'width 0.2s',
                    pointerEvents: 'none'
                  }}
                />


                {/* Visible Slider Thumb */}
                <div
                  style={{
                    width: '12px', height: '12px', background: accent,
                    borderRadius: '50%', position: 'absolute',
                    top: '-3px',
                    left: `calc(${pct === '' ? 0 : pct}% - 6px)`,
                    transition: 'left 0.2s', pointerEvents: 'none',
                    boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                  }}
                />
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={pct === '' ? 0 : pct}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    if (isReadOnly) return;
                    handleProgressChange(e.target.value);
                  }}
                  style={{
                    position: 'absolute', top: '-4px', left: 0, width: '100%', height: '14px',
                    opacity: 0, cursor: isReadOnly ? 'default' : 'ew-resize', margin: 0
                  }}
                  title={isReadOnly ? "" : "Geser untuk mengubah progress"}
                />
              </div>
            </div>

            {/* Action – Hapus */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {!isReadOnly && (
                <button
                  onClick={() => handleDelete(task)}
                  style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    background: 'transparent', border: '1px solid transparent', // default transparent
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.2s',
                    color: '#94a3b8' // soft grey default
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#fef2f2';
                    e.currentTarget.style.color = '#ef4444';
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.borderColor = '#fecaca';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#94a3b8';
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                  title="Hapus Task"
                >
                  <Trash2 size={14} color="currentColor" />
                </button>
              )}
            </div>

            {/* Last Update */}
            <div style={{ textAlign: 'center', fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
              {task.lastUpdate || '-'}
            </div>
          </div>
        );
      })}

      {/* ── Add task input row ── */}
      {!isReadOnly && (
        <div
          style={{
            marginTop: '10px', borderRadius: '12px',
            border: inputFocused ? `1.5px solid ${accent}` : '1.5px solid #e2e8f0',
            background: inputFocused ? `${accent}06` : '#fafafa',
            boxShadow: inputFocused ? `0 0 0 3px ${accent}18` : 'none',
            padding: '12px 20px', transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}
        >
          <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: `${accent}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: '17px', color: accent, lineHeight: 1, fontWeight: '300' }}>+</span>
          </div>
          <input
            type="text"
            value={newTaskName}
            onChange={(e) => setNewTaskName(e.target.value)}
            onKeyDown={handleAdd}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            placeholder="Apa yang perlu di kerjakan?"
            style={{
              flex: 1, border: 'none', outline: 'none',
              background: 'transparent', fontSize: '13px', color: '#0f172a',
              fontFamily: "'Inter', sans-serif",
            }}
          />
          {newTaskName && (
            <span style={{ fontSize: '11px', color: '#94a3b8', flexShrink: 0 }}>Tekan Enter untuk menambah</span>
          )}
        </div>
      )}

      {/* ── Custom Delete Confirmation Popup ── */}
      {deleteConfirm && (
        <>
          <div
            onClick={() => setDeleteConfirm(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 9998,
              background: 'rgba(15, 23, 42, 0.4)',
              backdropFilter: 'blur(4px)',
              animation: 'fadeIn 0.2s ease',
            }}
          />
          <div
            style={{
              position: 'fixed', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)', zIndex: 9999,
              background: '#fff', borderRadius: '20px',
              padding: '32px 28px', width: '380px', maxWidth: '90vw',
              boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
              animation: 'fadeInScale 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
              textAlign: 'center',
            }}
          >
            {/* Warning Icon */}
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 4px 14px rgba(239, 68, 68, 0.15)',
            }}>
              <Trash2 size={24} color="#ef4444" />
            </div>

            <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: '700', color: '#0f172a', fontFamily: "'Inter', sans-serif" }}>
              Hapus Task?
            </h3>

            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#64748b', lineHeight: 1.5, fontFamily: "'Inter', sans-serif" }}>
              Anda akan menghapus task berikut:
            </p>

            {/* Task Name Badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '8px',
              background: '#f8fafc', border: '1px solid #e2e8f0',
              marginBottom: '32px',
            }}>
              <span style={{
                fontSize: '13px', fontWeight: '600', color: '#334155',
                maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                "{deleteConfirm.name}"
              </span>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  flex: 1, padding: '10px 20px', borderRadius: '10px',
                  border: '1.5px solid #e2e8f0', background: '#fff',
                  color: '#475569', fontSize: '13px', fontWeight: '600',
                  cursor: 'pointer', transition: 'all 0.2s',
                  fontFamily: "'Inter', sans-serif",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              >
                Batal
              </button>
              <button
                onClick={confirmDeleteTask}
                style={{
                  flex: 1, padding: '10px 20px', borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: '#fff', fontSize: '13px', fontWeight: '700',
                  cursor: 'pointer', transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.35)',
                  fontFamily: "'Inter', sans-serif",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.35)'; }}
              >
                Ya, Hapus
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListTab;
